import React, { useCallback, useMemo, useState } from 'react'
import { customPositionCellStyle, customPositionRowStyle } from '../../components/postionsWidget/BasketWisePostionContainer'
import { alpha, Box, Menu, MenuItem, TableCell, TableRow, Typography } from '@mui/material'
import { KeyboardArrowDownRounded, KeyboardArrowRightRounded } from '@mui/icons-material'
import { createStringDateFromStamp, createStringDateFromStampWithTime } from '../../util/tradePage'
import { OrderDataPrices, OrdersDataType } from '../../reducer/orders.reducer'
import { toLocaleStringIn } from '../../util/general'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../components/custom'
import { OptionType, Security } from '../../proto/generated/security'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../selectors/home'
import { getSecurityKey } from '../../util/security'
import { selectSimulatorOrderLiveData, selectSimulatorOrdersLiveData, selectStrikeToOptions, selectStrikeToOptionsMap, selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { setSimulatorOrderDataFunction } from '../../reducer/simulator.reducer'
import { v4 as uuid } from 'uuid'
import { getCurrentOrderLtpPriceFromData } from '../../util/tradepanel'
import { selectSimulatorDate, selectSimulatorOrderById } from '../../selectors/simulator'
import { optionTradeDataType } from '../typings/options.typings'
import { formatGreeks } from '../home/optionChainViewTabs/components/DynamicColumnRender'
import { calculateGcd } from '../../util/orders'
import { calculateSimulatorOrdersLiveData } from '../../reducer/options.reducer'
import { ActionType, useUndoRedoContext } from '../../components/UndoRedoFunctonalities/UndoRedo.context'
import { findSimulatorSquareOffOrder } from '../../util/options'
import commonActions from '../../actions/common.actions'
import { useDispatch } from 'react-redux'
import ExitIcon from '../../assets/icons/ExitIcon'

type Props = {
    showGreeks: boolean
    timeStamp: number
    position: {
        order: OrdersDataType
        orderPrice: OrderDataPrices
    }[]
    showAll: boolean
}

const TimeWisePositions = ({ showGreeks, timeStamp, position, showAll }: Props) => {
    const [open, setOpen] = useState(true)
    const dispatch = useDispatch()
    const { push } = useUndoRedoContext()
    const { orders, ordersPrices } = useCombineBasketAnalyzeAndLegOrders()
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const { primary, underlying } = useSelector(selectPrimarySecurity)
    const security = underlying || primary
    const underlyingToken = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const ordersLiveData = useSelector(selectSimulatorOrdersLiveData)
    const strikeToOptionMap = useSelector(selectStrikeToOptionsMap(underlyingToken))
    const sqOffByOrderIdToOrderMap = useMemo(() => {
        const map: Record<string, OrdersDataType> = {}
        orders.forEach(order => {
            if (order.sqOffByOrderId) {
                const sqOrder = findSimulatorSquareOffOrder(order, orders, simulatorTimestamp)
                if(sqOrder) {
                    map[order.sqOffByOrderId] = sqOrder
                }
            }
        })
        return map
    }, [orders, simulatorTimestamp])

    

    const handleSquareOffAll = useCallback(() => {
        let newOrders = [...orders]
        let newOrdersPrices = [...ordersPrices]
        const prevSqOffIdsToBeRemoved: string[] = []
        position.forEach(p => {
            let prevSqOffByOrderId: string | undefined = undefined
            const sqOrder = sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']
            if(sqOrder) return
            const orderIndex = newOrders.findIndex(o => o.uuid === p.order.uuid)
            const strikeData = strikeToOptionMap[`${p.order.expiry}-${p.order.security?.strikePrice}`]
            const security = p.order.security
            if (strikeData && security) {
                const optionData = security.optionType === OptionType.CE ? strikeData.callOption : strikeData.putOption
                if (optionData) {
                    prevSqOffByOrderId = newOrders[orderIndex].sqOffByOrderId
                    const type = p.order.type === 'buy' ? 'sell' : 'buy'
                    newOrders = [...newOrders, {
                        type,
                        security: security,
                        data: strikeData,
                        callput: security.optionType === OptionType.CE ? 'call' : 'put',
                        quantity: newOrders[orderIndex].quantity,
                        expiry: security.expiry?.expiry ?? 0,
                        strike: strikeData.strikeCol.strike,
                        orderDetails: undefined,
                        uuid: uuid(),
                        contractType: 'OPTION',
                        visibility: true,
                        timeStamp: simulatorTimestamp,
                        traded: true,
                        sqOffWithOrderId: newOrders[orderIndex].uuid
                    }]

                    const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionsTradesData ?? [], [])
                    newOrdersPrices = [...newOrdersPrices, {
                        ...newPrice,
                        traded: true
                    }]

                    newOrders[orderIndex] = {
                        ...newOrders[orderIndex],
                        sqOffByOrderId: newOrders[newOrders.length - 1].uuid
                    }
                    
                    if (prevSqOffByOrderId && prevSqOffByOrderId !== newOrders[orderIndex].sqOffByOrderId) {
                        prevSqOffIdsToBeRemoved.push(prevSqOffByOrderId)
                    }
                }
            }
        })
        if(prevSqOffIdsToBeRemoved.length > 0) {
            const indexesToRemove = new Set<number>();
            prevSqOffIdsToBeRemoved.forEach(prevSqOffByOrderId => {
                const index = newOrders.findIndex(o => o.uuid === prevSqOffByOrderId)
                if (index !== -1) {
                    indexesToRemove.add(index);
                }
            })
            newOrders = newOrders.filter((_, index) => !indexesToRemove.has(index));
            newOrdersPrices = newOrdersPrices.filter((_, index) => !indexesToRemove.has(index));
            //     commonActions.setApiError(dispatch, {
            //         error: true,
            //         errorType: 'info',
            //         message: 'This order was squared off by another order, removing the previous square off order.'
            //     })
        }
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simulator orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders: newOrders, orderPrices: newOrdersPrices })
        calculateSimulatorOrdersLiveData(underlyingToken)
    }, [optionsTradesData, orders, ordersPrices, position, push, simulatorTimestamp, sqOffByOrderIdToOrderMap, strikeToOptionMap, underlyingToken])

    const handleSquareOff = useCallback((security: Security | undefined, type: 'sell' | 'buy', strikeData: optionTradeDataType, orderId: string, sqQuantity: number) => {
        const newOrders = [...orders]
        const newOrdersPrices = [...ordersPrices]
        let prevSqOffByOrderId: string | undefined = undefined
        if (strikeData && security) {
            const optionData = security.optionType === OptionType.CE ? strikeData.callOption : strikeData.putOption
            if (optionData) {
                const orderIndex = newOrders.findIndex(o => o.uuid === orderId)
                prevSqOffByOrderId = newOrders[orderIndex].sqOffByOrderId
                if (sqQuantity === newOrders[orderIndex].quantity) {
                    newOrders.push({
                        type,
                        security: security,
                        data: strikeData,
                        callput: security.optionType === OptionType.CE ? 'call' : 'put',
                        quantity: sqQuantity,
                        expiry: security.expiry?.expiry ?? 0,
                        strike: strikeData.strikeCol.strike,
                        orderDetails: undefined,
                        uuid: uuid(),
                        contractType: 'OPTION',
                        visibility: true,
                        timeStamp: simulatorTimestamp,
                        traded: true,
                        sqOffWithOrderId: orderId
                    })

                    const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionsTradesData ?? [], [])
                    newOrdersPrices.push({
                        ...newPrice,
                        traded: true
                    })

                    newOrders[orderIndex] = {
                        ...newOrders[orderIndex],
                        sqOffByOrderId: newOrders[newOrders.length - 1].uuid
                    }
                } else {
                    const newOrderId = uuid()
                    newOrders[orderIndex] = {
                        ...newOrders[orderIndex],
                        quantity: newOrders[orderIndex].quantity - sqQuantity
                    }
                    const newOrder = {
                        ...newOrders[orderIndex],
                        quantity: sqQuantity,
                        uuid: newOrderId,
                        contractType: 'OPTION',
                        visibility: true,
                        traded: true,
                    } as OrdersDataType
                    const newOrderPrice = {
                        ...newOrdersPrices[orderIndex]
                    }
                    newOrders.splice(orderIndex + 1, 0, newOrder)
                    newOrdersPrices.splice(orderIndex + 1, 0, newOrderPrice)

                    newOrders.push({
                        type,
                        security: security,
                        data: strikeData,
                        callput: security.optionType === OptionType.CE ? 'call' : 'put',
                        quantity: sqQuantity,
                        expiry: security.expiry?.expiry ?? 0,
                        strike: strikeData.strikeCol.strike,
                        orderDetails: undefined,
                        uuid: uuid(),
                        contractType: 'OPTION',
                        visibility: true,
                        timeStamp: simulatorTimestamp,
                        traded: true,
                        sqOffWithOrderId: newOrderId
                    })

                    const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionsTradesData ?? [], [])
                    newOrdersPrices.push({
                        ...newPrice,
                        traded: true
                    })

                    newOrders[orderIndex + 1] = {
                        ...newOrders[orderIndex + 1],
                        sqOffByOrderId: newOrders[newOrders.length - 1].uuid
                    }
                }
                if (prevSqOffByOrderId && prevSqOffByOrderId !== newOrders[orderIndex].sqOffByOrderId) {
                    const prevSqOffOrderIndex = newOrders.findIndex(o => o.uuid === prevSqOffByOrderId)
                    if (prevSqOffOrderIndex !== -1) {
                        newOrders.splice(prevSqOffOrderIndex, 1)
                        newOrdersPrices.splice(prevSqOffOrderIndex, 1)
                        commonActions.setApiError(dispatch, {
                            error: true,
                            errorType: 'info',
                            message: 'This order was squared off by another order, removing the previous square off order.'
                        })
                    }
                }
            }
            push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simulator orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
            setSimulatorOrderDataFunction({ orders: newOrders, orderPrices: newOrdersPrices })
            calculateSimulatorOrdersLiveData(underlyingToken)
        }
    }, [orders, ordersPrices, push, underlyingToken, simulatorTimestamp, optionsTradesData, dispatch])

    const requiredPositions = useMemo(() => {
        return showAll ? position : position.filter(p => {
            const sqOrder = sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']
            return !sqOrder
        })
    }, [position, showAll, sqOffByOrderIdToOrderMap])

    const StrategyQuantity = useMemo(() => {
        return calculateGcd(
            requiredPositions.map((p) => {
                return p.order.quantity * (p.order.security?.lotSize ?? 0)
            })
        )
    }, [requiredPositions])

    const StrategyAvgPrice = useMemo(() => {
        let result = 0;
        requiredPositions.forEach((p) => {
            if (p.order.type === 'buy') {
                result -= (Number(p.orderPrice?.price ?? 0) * p.order.quantity * (p.order.security?.lotSize ?? 0))
            } else {
                result += (Number(p.orderPrice?.price ?? 0) * p.order.quantity * (p.order.security?.lotSize ?? 0))
            }
        })
        result /= StrategyQuantity
        return toLocaleStringIn(result)
    }, [StrategyQuantity, requiredPositions])

    const StrategyQuantityRender = useMemo(() => {
        if (showGreeks) {
            let delta = 0;
            requiredPositions.forEach((p) => {
                const sqOrder = sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']
                if (!sqOrder) {
                    const orderLiveData = ordersLiveData?.[p.order.uuid]
                    if (orderLiveData) {
                        delta += (orderLiveData?.optionGreeks?.delta ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                    }
                }
            })
            return formatGreeks(delta)
        }
        return StrategyQuantity
    }, [showGreeks, StrategyQuantity, requiredPositions, sqOffByOrderIdToOrderMap, ordersLiveData])

    const StrategyAvgPriceRender = useMemo(() => {
        if (showGreeks) {
            let theta = 0;
            requiredPositions.forEach((p) => {
                const sqOrder = sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']
                if (!sqOrder) {
                    const orderLiveData = ordersLiveData?.[p.order.uuid]
                    if (orderLiveData) {
                        theta += (orderLiveData?.optionGreeks?.theta ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                    }
                }
            })
            return formatGreeks(theta)
        }
        return StrategyAvgPrice
    }, [showGreeks, StrategyAvgPrice, requiredPositions, sqOffByOrderIdToOrderMap, ordersLiveData])

    const StrategyLtpPrice = useMemo(() => {
        if (showGreeks) {
            let gamma = 0;
            requiredPositions.forEach((p) => {
                const sqOrder = sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']
                if (!sqOrder) {
                    const orderLiveData = ordersLiveData?.[p.order.uuid]
                    if (orderLiveData) {
                        gamma += (orderLiveData?.optionGreeks?.gamma ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                    }
                }
            })
            return formatGreeks(gamma)
        }
        let result = 0;
        requiredPositions.forEach((p) => {
            const orderLiveData = ordersLiveData?.[p.order.uuid]
            if (orderLiveData) {
                if (p.order.type === 'buy') {
                    result -= (orderLiveData?.ltp * p.order.quantity * (p.order.security?.lotSize ?? 0)) / (p.order.security?.priceMultiplier ?? 100)
                } else {
                    result += (orderLiveData?.ltp * p.order.quantity * (p.order.security?.lotSize ?? 0)) / (p.order.security?.priceMultiplier ?? 100)
                }
            }
        })
        result /= StrategyQuantity
        return toLocaleStringIn(result)
    }, [showGreeks, requiredPositions, StrategyQuantity, sqOffByOrderIdToOrderMap, ordersLiveData])

    const strategyProfitLoss = useMemo(() => {
        if (showGreeks) {
            let vega = 0;
            requiredPositions.forEach((p) => {
                const sqOrder = sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']
                if (!sqOrder) {
                    const orderLiveData = ordersLiveData?.[p.order.uuid]
                    if (orderLiveData) {
                        vega += (orderLiveData?.optionGreeks?.vega ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 1))
                    }
                }
            })
            return vega
        }
        let profit = 0;
        requiredPositions.forEach((p) => {
            const orderLiveData = ordersLiveData?.[p.order.uuid]
            if (orderLiveData) {
                const squareOffPrice = (orderLiveData?.ltp ?? 0) / (p.order.security?.priceMultiplier ?? 100)
                const pnlValue = (Number(squareOffPrice ?? 0) - Number(p.orderPrice?.price ?? 0)) * (p.order.quantity * (p.order.security?.lotSize ?? 0))
                profit += (p.order.type === 'buy' ? pnlValue : -pnlValue)
            }
        })
        return profit
    }, [showGreeks, requiredPositions, sqOffByOrderIdToOrderMap, ordersLiveData])

    return (
        <>
            <TableRow
                onClick={() => setOpen(!open)}
                sx={{
                    ...customPositionRowStyle,
                    position: 'relative',
                    height: '38px',
                    '&:hover': {
                        '.hover-button': {
                            visibility: 'visible'
                        }
                    },
                }}
            >
                <TableCell sx={{ cursor: 'pointer', width: '5%', padding: 0, textAlign: 'center' }}>
                    {open ? <KeyboardArrowDownRounded style={{ height: '20px', fontSize: '20px' }} /> :
                        <KeyboardArrowRightRounded style={{ height: '20px', fontSize: '20px' }} />}
                </TableCell>
                <TableCell
                    sx={{
                        padding: '5px',
                        // width: '35%'
                    }}
                >{createStringDateFromStampWithTime(timeStamp)}</TableCell>
                <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}>
                    <Typography variant="button"
                        className={`${Number(StrategyAvgPrice) <= 0 ? 'green-text' : 'red-text'}`}>
                        {Number(StrategyAvgPrice) <= 0 ? 'B' : 'S'}
                    </Typography>
                </TableCell>
                <TableCell
                    style={{
                        ...customPositionCellStyle,
                        // width: showGreeks ? 'auto' : '10%',
                        // textDecoration: !showGreeks && basketWisePositions.positions.every((p) => !p.open) ? 'line-through' : ''
                    }}
                >
                    {StrategyQuantityRender}
                </TableCell>
                <TableCell
                    style={customPositionCellStyle}
                >
                    {showGreeks ? formatGreeks(Number(StrategyAvgPriceRender)) : toLocaleStringIn(Math.abs(Number(StrategyAvgPriceRender)))}
                </TableCell>
                <TableCell
                    style={customPositionCellStyle}
                >
                    {showGreeks ? formatGreeks(Number(StrategyLtpPrice)) : toLocaleStringIn(Number(StrategyAvgPrice) <= 0 ? (-1 * Number(StrategyLtpPrice)) : Number(StrategyLtpPrice))}
                </TableCell>
                <TableCell
                    style={customPositionCellStyle}
                    className={`${showGreeks ? '' : strategyProfitLoss < 0 ? 'red-text' : strategyProfitLoss > 0 ? 'green-text' : ''}`}
                >
                    {showGreeks ? formatGreeks(strategyProfitLoss) : strategyProfitLoss === 0 ? '-' : toLocaleStringIn(strategyProfitLoss)}
                </TableCell>
                {
                    position.every(p => sqOffByOrderIdToOrderMap[p.order.sqOffByOrderId??'']) ? null :
                        <NonInteractiveTooltip title={'Square off'} arrow placement='left'>
                            <Box className='hover-button' sx={{
                                visibility: 'hidden',
                                position: 'absolute',
                                right: '0px',
                                top: '0px',
                                height: '100%',
                                display: 'flex',
                                background: theme => alpha(theme.palette.background.paper, 0.7)
                            }}>
                                <ExtraSmallButton
                                    sx={{
                                        width: '30px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleSquareOffAll()
                                        // handleSquareOff(p.order.security, p.order.type === 'buy' ? 'sell' : 'buy', strikeToOption, p.order.uuid, quantityDropDown)
                                    }}
                                ><ExitIcon fontSize='small' /></ExtraSmallButton>
                            </Box>
                        </NonInteractiveTooltip>
                }
            </TableRow>
            {!open ? null :
                position.map((p) => <RenderPositonRow
                    showAll={showAll}
                    showGreeks={showGreeks}
                    orderObj={p}
                    underlyingToken={underlyingToken}
                    key={p.order.uuid} // Use a unique key for each row
                    handleSquareOff={handleSquareOff}
                />)
            }
        </>
    )
}

const RenderPositonRow = ({ showAll, showGreeks, underlyingToken, orderObj: p, handleSquareOff }: {
    showAll: boolean, showGreeks: boolean, underlyingToken: string, orderObj: {
        order: OrdersDataType;
        orderPrice: OrderDataPrices;
    }
    handleSquareOff: (security: Security | undefined, type: 'sell' | 'buy', strikeData: optionTradeDataType, orderId: string, sqQuantity: number) => void
}) => {
    const [quantityDropDown, setQuantityDropDown] = useState(p.order.quantity)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const key = `${p.order.expiry}-${p.order.security?.strikePrice}`
    const strikeToOption = useSelector(selectStrikeToOptions(underlyingToken, key))
    const sqOffOrder = useSelector(selectSimulatorOrderById(p.order.sqOffByOrderId))
    const orderLiveData = useSelector(selectSimulatorOrderLiveData(p.order.uuid))

    const [pnl, squareOffPrice] = useMemo(() => {
        const squareOffPrice = (orderLiveData?.ltp ?? 0) / (p.order.security?.priceMultiplier ?? 100)
        const pnlValue = (Number(squareOffPrice ?? 0) - Number(p.orderPrice?.price ?? 0)) * (p.order.quantity * (p.order.security?.lotSize ?? 0))
        return [p.order.type === 'buy' ? pnlValue : -pnlValue, squareOffPrice]
    }, [orderLiveData, p])

    const { delta = 0, theta = 0, gamma = 0, vega = 0 } = useMemo(() => {
        return {
            delta: (orderLiveData?.optionGreeks?.delta ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 0)),
            theta: (orderLiveData?.optionGreeks?.theta ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 0)),
            gamma: (orderLiveData?.optionGreeks?.gamma ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 0)),
            vega: (orderLiveData?.optionGreeks?.vega ?? 0) * (p.order.quantity * (p.order.security?.lotSize ?? 0)),
        }
    }, [orderLiveData, p])

    const body = useMemo(() => (
        <TableRow
            sx={{
                borderBottom: '1.5px solid transparent',
                position: 'relative',
                opacity: sqOffOrder ? 0.7 : 1,
                '&:hover': {
                    '.hover-button': {
                        visibility: 'visible'
                    }
                },
                '&.MuiTableRow-root td:first-of-type': {
                    borderTopLeftRadius: '2px',
                    borderBottomLeftRadius: '2px'
                },
                '&.MuiTableRow-root td:last-of-type': {
                    borderTopRightRadius: '2px',
                    borderBottomRightRadius: '2px'
                }
            }}
        >
            <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}></TableCell>
            <TableCell
                sx={{
                    padding: '5px',
                    // width: '35%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    textWrap: 'nowrap'
                }}
            >
                <Typography color={'textSecondary'} sx={{ fontSize: '14px' }}>
                    {`${createStringDateFromStamp(p.order.security?.expiry?.expiry ?? 0)} ${p.order.contractType === 'OPTION' ? p.order.strike / (p.order.security?.priceMultiplier ?? 100) : ''} ${p.order.contractType === 'OPTION' ? p.order.callput === 'call' ? 'CE' : 'PE' : ''}`}
                </Typography>
            </TableCell>

            <TableCell sx={{ width: '5%', padding: 0, textAlign: 'center' }}>
                <Typography variant="button"
                    className={`${p.order.type === 'buy' ? 'green-text' : 'red-text'}`}
                    component="span">
                    {p.order.type === 'buy' ? 'B' : 'S'}
                </Typography>
            </TableCell>
            <TableCell
                style={{
                    ...customPositionCellStyle,
                    // width: '10% !important',
                    textDecoration: (showGreeks || !sqOffOrder) ? '' : 'line-through'
                }}
            >
                {
                    showGreeks ? formatGreeks(delta) : (p.order.quantity) * (p.order.security?.lotSize ?? 0)
                }
            </TableCell>
            <TableCell
                style={customPositionCellStyle}
            >
                {
                    showGreeks ? formatGreeks(theta) : toLocaleStringIn(Number(p.orderPrice?.price) ?? 0)
                }
            </TableCell>
            <TableCell
                style={customPositionCellStyle}
            >
                {
                    showGreeks ? formatGreeks(Number(gamma)) : toLocaleStringIn(Number(squareOffPrice) ?? 0)
                }
            </TableCell>
            <TableCell
                style={customPositionCellStyle}
                className={`${showGreeks || !Number.isFinite(pnl) || pnl === 0 ? '' : pnl < 0 ? 'red-text' : 'green-text'} font-feature-num`}
            >
                {
                    showGreeks ? formatGreeks(vega) : (Number.isFinite(pnl) && pnl !== 0) ? toLocaleStringIn(pnl) : '-'
                }
            </TableCell>
            {
                sqOffOrder ? null :
                    <NonInteractiveTooltip title={'Square off'} arrow placement='left'>
                        <Box className='hover-button' sx={{
                            visibility: Boolean(anchorEl) ? 'visible' : 'hidden',
                            position: 'absolute',
                            right: '0px',
                            top: '0px',
                            height: '100%',
                            display: 'flex',
                            background: theme => alpha(theme.palette.background.paper, 0.7)
                        }}>
                            {
                                (p.order.quantity > 1) ?
                                    <>
                                        <ExtraSmallButton
                                            onClick={(e) => {
                                                setAnchorEl(e.currentTarget)
                                            }}
                                        >
                                            {quantityDropDown}
                                        </ExtraSmallButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={() => setAnchorEl(null)}
                                        >
                                            {
                                                Array.from({ length: p.order.quantity }, (_, i) => i + 1).map((i) => {
                                                    return <MenuItem selected={quantityDropDown === i} onClick={() => setQuantityDropDown(i)}>
                                                        {i}
                                                    </MenuItem>
                                                })
                                            }
                                        </Menu>
                                    </>
                                    : null
                            }
                            <ExtraSmallButton
                                sx={{
                                    width: '30px'
                                }}
                                onClick={() => handleSquareOff(p.order.security, p.order.type === 'buy' ? 'sell' : 'buy', strikeToOption, p.order.uuid, quantityDropDown)}
                            ><ExitIcon fontSize='small' /></ExtraSmallButton>
                        </Box>
                    </NonInteractiveTooltip>
            }
        </TableRow>
    ), [anchorEl, delta, gamma, handleSquareOff, p, pnl, quantityDropDown, showGreeks, sqOffOrder, squareOffPrice, strikeToOption, theta, vega])

    if (!showAll && sqOffOrder) return null

    return sqOffOrder ? (
        <NonInteractiveTooltip
            placement="left"
            arrow={true}
            title={`Squared off at ${createStringDateFromStampWithTime(sqOffOrder.timeStamp ?? 0)}`}
        >
            {body}
        </NonInteractiveTooltip>
    ) : body
}

export default TimeWisePositions